// converted flutter logic to js
interface Suggestion {
  title: string;
}

export class FuzzySearch {
  // Levenshtein Distance Function
  levenshteinDistance(s1: string, s2: string): number {
    const m = s1.length;
    const n = s2.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () =>
      Array(n + 1).fill(0),
    );

    for (let i = 0; i <= m; i++) {
      for (let j = 0; j <= n; j++) {
        if (i === 0) {
          dp[i][j] = j;
        } else if (j === 0) {
          dp[i][j] = i;
        } else if (s1[i - 1].toLowerCase() === s2[j - 1].toLowerCase()) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
      }
    }

    return dp[m][n];
  }

  // Similarity Score (1 - normalized Levenshtein distance)
  similarityScore(s1: string, s2: string): number {
    const distance = this.levenshteinDistance(s1, s2);
    const maxLength = Math.max(s1.length, s2.length);
    return maxLength === 0 ? 1.0 : 1 - distance / maxLength;
  }

  // Prefix Bonus (bonus for matching prefix)
  prefixBonus(s1: string, s2: string): number {
    let matchLength = 0;
    const minLength = Math.min(s1.length, s2.length);

    while (
      matchLength < minLength &&
      s1[matchLength].toLowerCase() === s2[matchLength].toLowerCase()
    ) {
      matchLength++;
    }

    return (matchLength / minLength) * 0.5;
  }

  // Best Matches: Returns top 'limit' matches based on score
  bestMatches(input: string, options: string[], limit: number): string[] {
    const scoredOptions = options.map((option) => {
      const score =
        this.similarityScore(input, option) + this.prefixBonus(input, option);
      return { option, score };
    });

    scoredOptions.sort((a, b) => b.score - a.score);

    return scoredOptions.slice(0, limit).map((entry) => entry.option);
  }

  // Best Match: Returns the single best match
  bestMatch(input: string, options: string[]): string {
    return options.reduce((best, option) => {
      const score = this.similarityScore(input, option);
      return score > this.similarityScore(input, best) ? option : best;
    }, "");
  }
}
